import i18next from 'i18next';
import { resolveNotificationEventName } from './notification-inbox-row.js';

/**
 * Resolve in-app / toast title+body in the active UI language.
 * Delivery stores a single rendered locale; event keys + notificationEvents
 * catalog let the client re-localize without re-dispatch.
 *
 * @param {object} row mapped or raw notification
 * @param {{ title?: string, message?: string, t?: Function, language?: string }} [opts]
 * @returns {{ title: string, message: string }}
 */
export function resolveLocalizedNotificationCopy(row, opts = {}) {
    const storedTitle = String(opts.title ?? row?.title ?? '').trim();
    const storedMessage = String(opts.message ?? row?.message ?? row?.body ?? '').trim();
    const eventName = resolveNotificationEventName(row);
    const rawParams = {
        ...(row?.data?.i18n?.params && typeof row.data.i18n.params === 'object'
            ? row.data.i18n.params
            : {}),
        ...(row?.params && typeof row.params === 'object' ? row.params : {}),
    };
    delete rawParams.i18n;

    // Missing interpolations must not leak as literal "{{name}}" in toasts/inbox.
    const params = {};
    for (const [key, value] of Object.entries(rawParams)) {
        if (key === 'i18n' || value == null) continue;
        params[key] = value;
    }
    if (params.name == null) params.name = '';
    if (params.count == null) params.count = 0;

    const t = typeof opts.t === 'function'
        ? opts.t
        : (key, options) => i18next.t(key, {
            ns: 'notificationEvents',
            lng: opts.language,
            ...options,
        });

    if (!eventName) {
        return {
            title: stripUnresolvedPlaceholders(storedTitle),
            message: stripUnresolvedPlaceholders(storedMessage),
        };
    }

    const titleKey = `${eventName}.title`;
    const bodyKey = `${eventName}.body`;
    const localizedTitle = t(titleKey, { ...params, defaultValue: '' });
    const localizedBody = t(bodyKey, { ...params, defaultValue: '' });

    return {
        title: stripUnresolvedPlaceholders(
            (localizedTitle && localizedTitle !== titleKey)
                ? localizedTitle
                : storedTitle,
        ),
        message: stripUnresolvedPlaceholders(
            (localizedBody && localizedBody !== bodyKey)
                ? localizedBody
                : storedMessage,
        ),
    };
}

function stripUnresolvedPlaceholders(value) {
    return String(value || '')
        .replace(/\{\{\s*[\w.]+\s*\}\}/g, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+:/g, ':')
        .trim();
}
